/**
 * Promise-based wrapper over <ConfirmDialog>. Renders the dialog from
 * a single root-mounted Provider so any component can prompt the user
 * with one line:
 *
 *   const confirm = useConfirm();
 *   const ok = await confirm({
 *     title: 'Cancel subscription?',
 *     message: 'You will keep access until 9 Jun 2027.',
 *     confirmText: 'Cancel subscription',
 *     cancelText: 'Keep subscription',
 *     variant: 'danger',
 *   });
 *   if (!ok) return;
 *
 * Why this layer:
 *   - native window.confirm() is jarring before payment flows
 *   - the existing <ConfirmDialog> is controlled, which forces every
 *     consumer to manage open/close state + handlers per usage
 *
 * The Promise resolves to `true` on Confirm, `false` on Cancel /
 * Escape / backdrop click — same return-value contract as
 * window.confirm so existing call sites translate cleanly.
 */

import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import ConfirmDialog, { type ConfirmDetail, type ConfirmVariant } from './ConfirmDialog';

export type { ConfirmDetail, ConfirmVariant };

export interface ConfirmOptions {
  title: string;
  message?: React.ReactNode;
  details?: ConfirmDetail[];
  consequences?: React.ReactNode[];
  confirmText?: string;
  cancelText?: string;
  variant?: ConfirmVariant;
  hideCancel?: boolean;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmCtx = createContext<ConfirmFn | null>(null);

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmCtx);
  if (!ctx) {
    throw new Error('useConfirm must be used within <ConfirmProvider> - wrap your app root.');
  }
  return ctx;
}

interface PendingState {
  options: ConfirmOptions;
}

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [pending, setPending] = useState<PendingState | null>(null);
  const resolverRef = useRef<((v: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((options) => {
    return new Promise<boolean>((resolve) => {
      // If a previous dialog is still up (defensive — caller didn't
      // await), resolve the old one as cancelled so we don't leak a
      // dangling promise. New dialog takes over.
      if (resolverRef.current) {
        try {
          resolverRef.current(false);
        } catch (_) {
          /* noop */
        }
      }
      resolverRef.current = resolve;
      setPending({ options });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    const resolver = resolverRef.current;
    resolverRef.current = null;
    setPending(null);
    if (resolver) resolver(true);
  }, []);

  const handleCancel = useCallback(() => {
    const resolver = resolverRef.current;
    resolverRef.current = null;
    setPending(null);
    if (resolver) resolver(false);
  }, []);

  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      <ConfirmDialog
        isOpen={!!pending}
        title={pending?.options.title || ''}
        message={pending?.options.message}
        details={pending?.options.details}
        consequences={pending?.options.consequences}
        confirmText={pending?.options.confirmText}
        cancelText={pending?.options.cancelText}
        variant={pending?.options.variant || 'warning'}
        hideCancel={pending?.options.hideCancel}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </ConfirmCtx.Provider>
  );
};
